import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Transition,
} from '@headlessui/react'
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline'
import * as _ from 'lodash-es'
import { observer } from 'mobx-react'
import PropTypes from 'prop-types'
import React, {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useLocation, useNavigate, Link } from 'react-router-dom'

import authBackground from '@/assets/images/auth_image.webp'
import logoWhite from '@/assets/images/logo_white.svg'
import poweredBy from '@/assets/images/powered_by_horizontal.png'
import wordmark from '@/assets/images/wordmark.svg'
import { Select } from '@/components/forms/Select'
import { UserIcon } from '@/components/icons'
import { MfaPopover } from '@/components/shared/MfaPopover'
import { Notifications } from '@/components/shared/Notifications'
import { useNotificationToastQueue } from '@/hooks/NotificationToastQueue'
import { useWebSocket } from '@/hooks/Websocket'
import { logout } from '@/services/user.service'
import { DashboardStoreContext } from '@/stores/DashboardStore'
import { NotificationStoreContext } from '@/stores/NotificationStore'
import { RootStoreContext } from '@/stores/RootStore'
import { UserStoreContext } from '@/stores/UserStore'
import { HEADER_HEIGHT } from '@/utils/constants'
import { joinClassNames } from '@/utils/helpers'

const TYPES = [
  { label: 'WPC Managed', id: 'wpc' },
  { label: 'Dealer Managed', id: 'dealer' },
]

/**
 *
 * NavBar
 *
 */
const NavBar = observer(({ options }) => {
  // Context
  const navigate = useNavigate()
  const location = useLocation()
  const { clearStore } = useContext(RootStoreContext)
  const { type, setType } = useContext(DashboardStoreContext)
  const {
    setUnreadNotificationsCount,
    unreadNotificationsCount,
    setTriggerAlert,
    isMenuOpen,
    setHasNewNotifications,
  } = useContext(NotificationStoreContext)
  const {
    accessToken,
    isAuthenticated,
    isDealerUser,
    isImpersonating,
    isWpcExecutive,
    getUpdatedUser,
    refreshToken,
    shouldReceiveNotifications,
    setCurrentUser,
    user,
  } = useContext(UserStoreContext)

  // State
  const [showMfaReminder, setShowMfaReminder] = useState(false)

  // Refs
  const prevTypeRef = useRef(type) // To track previous type for navigation reset
  const isMenuOpenRef = useRef(isMenuOpen)

  const enqueueNotificationToast = useNotificationToastQueue()

  useEffect(() => {
    getUpdatedUser()
  }, [])

  useEffect(() => {
    // WARP-567 — dismissing this writes `hasViewedMfaNotification`, which an
    // impersonated session cannot do. Showing it would prompt the support person for an
    // action that fails and then keeps reappearing.
    if (user && user.hasViewedMfaNotification === false && isDealerUser && !isImpersonating) {
      setShowMfaReminder(true)
    }
  }, [user, isImpersonating])

  /**
   * Reset navigation to the dashboard if the type changes and if the user is a WPC Executive.
   */
  useEffect(() => {
    if (prevTypeRef.current !== type) {
      if (
        isWpcExecutive &&
        (location.pathname.includes('repair-order') || location.pathname.includes('dealer'))
      ) {
        navigate('/dashboard')
      }

      prevTypeRef.current = type
    }
  }, [type])

  useEffect(() => {
    isMenuOpenRef.current = isMenuOpen
  }, [isMenuOpen])

  /**
   * Set up the WebSocket connection to listen for notifications
   */
  const handleMessage = useCallback((msg) => {
    // Update the notification count. Notifications may be triggered by a variety of events.
    // We want to trigger an alert/animation of the notification count if the count increases
    // and the trigger is a new notification. Manually marking a message as unread will not
    // trigger the alert/animation.
    if (msg.type === 'unread_notifications_count') {
      if (
        msg.trigger === 'New Notification' && // Only trigger alert for new notifications
        unreadNotificationsCount !== null && // Handle initial load
        msg.count > unreadNotificationsCount // Only trigger alert if the count increases
      ) {
        setTriggerAlert(true)
      }
      setUnreadNotificationsCount(msg.count)
    } else if (msg.type === 'new_notification' && !isMenuOpenRef.current) {
      enqueueNotificationToast({
        linkLabel: msg.data.description,
        url: `/dealer/${msg.data.data.dealerId}/repair-order/${msg.data.data.roId}`,
      })
    } else if (msg.type === 'new_notification') {
      setHasNewNotifications(true)
    }
  }, [])

  useWebSocket({
    enabled: shouldReceiveNotifications && Boolean(user.id),
    path: user.id ? `v1/notifications/${user.id}` : null,
    token: accessToken,
    onMessage: handleMessage,
    config: {
      debug: true,
      maxRetries: 10,
    },
    key: user.id, // Force reconnect when user.id changes
  })

  /**
   * Filter nav options based on the type of dashboard selected (either WPC Managed or Dealer Managed).
   */
  const navOptions = useMemo(() => {
    const filteredOptions = options.filter((o) => (o.type && o.type === type) || !o.type)

    return _.map(filteredOptions, (option) => {
      const isActive = location.pathname === option.path
      const baseClasses = joinClassNames(
        isActive
          ? 'text-gold-300 border-gold-300 border-b-2'
          : 'hover:border-gold-300 hover:text-gold-300 border-b-2 border-transparent text-white',
        'group flex items-center px-2 py-2 text-sm font-medium',
      )

      // Use Link if path exists
      if (option.path) {
        return (
          <Link key={option.label} to={option.path} className={baseClasses}>
            {option.label}
          </Link>
        )
      }

      // Otherwise, use button with onClick
      return (
        <button key={option.label} type="button" className={baseClasses} onClick={option.onClick}>
          {option.label}
        </button>
      )
    })
    // `options` MUST be a dependency: it's rebuilt by Routes as the feature flags
    // (global RO list, Analytics) and the user's type resolve asynchronously.
    // Without it the bar kept rendering the first-paint list and only picked up
    // the new entries once a navigation changed location.pathname — i.e. the nav
    // items "needed a click to show up" (WARP-888).
  }, [options, type, location.pathname])

  // If the user is not authenticated, render a basic nav bar
  if (!isAuthenticated) {
    return (
      <div className="bg-background relative mb-10 flex h-[150px] w-screen place-content-center items-center md:mb-0 md:h-[200px] lg:h-[290px] xl:h-[290px]">
        <img
          aria-hidden="true"
          className="absolute inset-x-0 top-0 z-0 min-h-[20vh] w-full object-cover"
          src={authBackground}
          alt="Background"
        />

        <div className="flex w-full flex-col items-center justify-center gap-2">
          <img
            aria-hidden="true"
            className="z-10 mt-4 h-8 sm:h-10 md:h-12 lg:h-16"
            src={wordmark}
            alt="Logo"
            onClick={() => navigate('/')}
          />
          <img
            aria-hidden="true"
            className="z-10 mt-4 h-6"
            src={poweredBy}
            alt="Powered By Warranty Processing"
            onClick={() => navigate('/')}
          />
        </div>
      </div>
    )
  }

  return (
    <div className="w-screen">
      <Disclosure as="nav" className="bg-midnight">
        {({ open }) => (
          <>
            <div className="px-2 sm:px-6 lg:px-8">
              <div
                className="relative flex justify-between"
                style={{ height: `${HEADER_HEIGHT}px` }}
              >
                {/* Mobile menu */}
                <div className="absolute inset-y-0 left-0 flex items-center sm:hidden">
                  <DisclosureButton className="inline-flex items-center justify-center rounded-md p-2 hover:bg-gray-100 focus:outline-none ">
                    <span className="sr-only">Open main menu</span>
                    {open ? (
                      <XMarkIcon className="stroke-blue-dark block size-6" aria-hidden="true" />
                    ) : (
                      <Bars3Icon className="stroke-blue-dark block size-6" aria-hidden="true" />
                    )}
                  </DisclosureButton>
                </div>

                {/* Navigation Options */}
                <div className="flex flex-1 items-center justify-center sm:items-stretch sm:justify-start">
                  {/* Logo */}
                  <Link className="flex shrink-0 cursor-pointer items-center" to="/">
                    <img className="h-6 w-auto lg:block" src={logoWhite} alt="Logo" />
                  </Link>

                  {isWpcExecutive && (
                    <div className="ml-6 flex justify-center">
                      <Select
                        customButton={
                          <div className="bg-blue-light h-fit w-32 rounded-full px-3 py-1 text-xs font-semibold uppercase leading-tight text-blue-950 focus:outline-none">
                            <span className="text-blue-950">
                              {TYPES.find((o) => o.id === type).label}
                            </span>
                          </div>
                        }
                        options={TYPES}
                        value={TYPES.find((o) => o.id === type)}
                        onChange={(o) => setType(o.id)}
                        style={{ alignContent: 'center' }}
                      />
                    </div>
                  )}

                  {/* Options */}
                  <div className="hidden sm:ml-6 sm:flex sm:space-x-8">{navOptions}</div>
                </div>

                {/* Profile dropdown */}
                <div className="absolute inset-y-0 right-0 flex items-center gap-2 pr-2 sm:static sm:inset-auto sm:ml-6 sm:pr-0">
                  {/* Always render the bell: site-wide announcements must reach users
                      even when they've turned off their own in-app notifications. */}
                  <Notifications />

                  <Menu as="div" className="relative">
                    <MfaPopover
                      user={user}
                      setCurrentUser={setCurrentUser}
                      show={showMfaReminder}
                      setShow={setShowMfaReminder}
                    >
                      <div>
                        <MenuButton
                          className="flex rounded-full bg-transparent text-sm focus:outline-none"
                          data-testid="user-dropdown"
                        >
                          <span className="sr-only">Open user menu</span>
                          <UserIcon className="block size-8 fill-white" />
                        </MenuButton>
                      </div>
                    </MfaPopover>
                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-200"
                      enterFrom="transform opacity-0 scale-95"
                      enterTo="transform opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="transform opacity-100 scale-100"
                      leaveTo="transform opacity-0 scale-95"
                    >
                      <MenuItems className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-none">
                        {_.map(
                          [
                            { label: 'Profile', path: '/profile' },
                            { label: 'Settings', path: '/settings' },
                            {
                              label: 'Sign Out',
                              onClick: () => logout(refreshToken, clearStore),
                            },
                          ],
                          (option) => (
                            <MenuItem key={option.label}>
                              {({ active: a }) => {
                                const baseClasses = joinClassNames(
                                  a ? 'bg-gray-100' : '',
                                  'block w-full px-4 py-2 text-start text-sm text-gray-700',
                                )

                                // Use Link if path exists
                                if (option.path) {
                                  return (
                                    <Link className={baseClasses} to={option.path}>
                                      {option.label}
                                    </Link>
                                  )
                                }

                                // Otherwise, use button with onClick
                                return (
                                  <button
                                    type="button"
                                    className={baseClasses}
                                    onClick={option.onClick}
                                  >
                                    {option.label}
                                  </button>
                                )
                              }}
                            </MenuItem>
                          ),
                        )}
                      </MenuItems>
                    </Transition>
                  </Menu>
                </div>
              </div>
            </div>

            {/* Mobile menu */}
            <DisclosurePanel className="sm:hidden">
              <div className="space-y-1 pb-4 pt-2">
                {_.map(options, (option) => (
                  <DisclosureButton
                    as="a"
                    href={option.path}
                    className={joinClassNames(
                      'block border-l-4 py-2 pl-3 pr-4 text-base font-medium',
                      location.pathname === option.path
                        ? 'border-gold-300 text-gold-300'
                        : 'hover:border-gold-300 hover:text-gold-300 border-transparent text-white',
                    )}
                    key={option.label}
                  >
                    {option.label}
                  </DisclosureButton>
                ))}
              </div>
            </DisclosurePanel>
          </>
        )}
      </Disclosure>
    </div>
  )
})

NavBar.propTypes = {
  menuOptions: PropTypes.arrayOf(PropTypes.object),
  options: PropTypes.arrayOf(PropTypes.object),
  orientation: PropTypes.string,
}

NavBar.defaultProps = {
  menuOptions: null,
  options: null,
  orientation: 'horizontal',
}

export default NavBar
